<template>
    <v-dialog v-model="open" :fullscreen="$vuetify.breakpoint.xs" :max-width="$vuetify.breakpoint.width > 1830 ? 1780 : 960" @click:outside="close">
        <v-card :disabled="isConsultation">
            <v-container fluid>
                <MessageOverlay ref="overlay" />
                <v-form ref="form" v-model="isFormValid" lazy-validation>
                    <v-row justify="center">
                        <v-col cols="auto">
                            <v-skeleton-loader
                                v-if="loading"
                                :width="webImageWidth"
                                :height="webImageHeight"
                                type="image"
                            ></v-skeleton-loader>
                            <v-hover>
                                <template v-slot:default="{ hover }">
                                    <canvas
                                        id="canvasOutput"
                                        :width="webImageWidth"
                                        :height="webImageHeight"
                                        style="background-color:black"
                                        :style="loading ? 'display: none' : hover ? 'cursor: crosshair' : ''"
                                        @click="drawOnImage"
                                    ></canvas>
                                </template>
                            </v-hover>
                            <img id="canvasInput" style="display: none" :width="utcImageWidth" :height="utcImageHeight" @load="loadOutput">
                        </v-col>
                        <v-col cols="auto" v-if="$vuetify.breakpoint.width > 1830" class="pr-0">
                            <div class="my-2">
                                <v-tooltip top>
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn v-bind="attrs" v-on="on"
                                            fab
                                            class="gradient-wintech"
                                            @click="selectRegionTool"
                                            :small="tab !== 0"
                                        ><v-icon>mdi-vector-square-plus</v-icon></v-btn>
                                    </template>                                 
                                    <span>{{ $vuetify.lang.t('$vuetify.utc.controlsCard.advancedOptionsDetail.addaregionofinterest') }}</span>
                                </v-tooltip>
                            </div>
                            <div class="my-2">
                                <v-tooltip top>
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn v-bind="attrs" v-on="on"
                                            fab
                                            class="gradient-wintech"
                                            @click="selectPointTool"
                                            :small="tab !== 1"
                                        ><v-icon>mdi-vector-point-plus</v-icon></v-btn>
                                    </template>                                 
                                    <span>{{ $vuetify.lang.t('$vuetify.utc.controlsCard.advancedOptionsDetail.addapointofinterest') }}</span>
                                </v-tooltip>
                            </div>
                        </v-col>
                        <v-col>
                            <v-tabs v-model="tab" fixed-tabs @change="changeTool" class="mb-2">
                                <v-tab>Regions ({{ regionNames.length }}/5)</v-tab>
                                <v-tab>Points ({{ pointNames.length }}/10)</v-tab>
                            </v-tabs>
                            <v-tabs-items v-model="tab" @change="changeTool">
                                <v-tab-item>
                                    <v-container> 
                                        <v-row justify="space-around" class="mb-4" :dense="$vuetify.breakpoint.smAndDown">
                                            <v-col v-if="$vuetify.breakpoint.mdAndUp" cols="3" align-self="center">
                                                <h4>{{ $vuetify.lang.t('$vuetify.utc.controlsCard.advancedOptionsDetail.regionmanagement') }} ({{ regionNames.length }}/5)</h4>
                                            </v-col>
                                            <v-col cols="auto" v-for="n in 5" :key="n">
                                                <div class="label-on-top">
                                                    <v-label>R{{ n }}</v-label>
                                                </div>
                                                <v-checkbox
                                                    v-model="regionsToDelete"
                                                    :value="'' + n"
                                                    :disabled="!regionNames.includes('R' + n)"
                                                    :off-icon="!regionNames.includes('R' + n) ? 'mdi-square' : '$checkboxOff'"
                                                    :dense="$vuetify.breakpoint.smAndDown"
                                                    hide-details
                                                    class="mt-0"
                                                ></v-checkbox>
                                            </v-col>
                                            <v-col cols="auto" align-self="center">
                                                <v-tooltip top>
                                                    <template v-slot:activator="{ on, attrs }">
                                                        <v-btn v-bind="attrs" v-on="on"                                
                                                            color="red"
                                                            @click="deleteRegion"
                                                            :disabled="disableDeleteRegion"
                                                        ><v-icon>mdi-delete</v-icon></v-btn>
                                                    </template>
                                                    <span>{{ $vuetify.lang.t('$vuetify.utc.controlsCard.advancedOptionsDetail.deleteregion') }}</span>
                                                </v-tooltip>
                                            </v-col>
                                        </v-row>
                                        <v-divider class="mb-4"></v-divider>
                                        <v-row justify="center">
                                            <v-col cols="auto">
                                                <h4>{{ $vuetify.lang.t('$vuetify.utc.controlsCard.advancedOptionsDetail.alertmanagement') }} ({{this.regionRulesData.length + this.pointRulesData.length}}/{{nbMaxAlert}})</h4>
                                            </v-col>
                                        </v-row>
                                        <v-row justify="space-around">
                                            <v-col cols="12" md="6"> <!-- Depassement de seuil -->
                                                <v-row justify="center" class="mb-2">
                                                    <v-col cols="auto">
                                                        <v-btn 
                                                            :small="$vuetify.breakpoint.xs" 
                                                            :class="this.regionRulesData.length + this.pointRulesData.length >= nbMaxAlert ? '':'gradient-wintech'" 
                                                            :disabled="this.regionRulesData.length + this.pointRulesData.length >= nbMaxAlert"
                                                            @click="addNewRegionThresholdRule"
                                                        >
                                                            <v-icon left>mdi-bell-plus-outline</v-icon>
                                                            {{ $vuetify.lang.t('$vuetify.utc.controlsCard.advancedOptionsDetail.thresholdexceedancealert') }}
                                                        </v-btn>
                                                    </v-col>
                                                </v-row>
                                                <v-card class="overflow-y-auto overflow-x-hidden" :height="$vuetify.breakpoint.xs ? 'auto' : 280" max-height="280" flat>
                                                    <template v-if="regionThresholdRules.length > 0">
                                                        <v-row v-for="(rule, index) in regionThresholdRules" :key="index" justify="center" class="mt-1" dense>
                                                            <v-col cols="4">
                                                                <v-select
                                                                    v-model="rule.items"
                                                                    :items="regionNames"
                                                                    :rules="selectThresholdRule"
                                                                    label="Region*"
                                                                    dense
                                                                    outlined
                                                                ></v-select>
                                                            </v-col>
                                                            <v-col cols="4">
                                                                <v-text-field
                                                                    v-model="rule.value"
                                                                    :label="$vuetify.lang.t('$vuetify.utc.controlsCard.advancedOptionsDetail.threshold') + ' (°C)*'"
                                                                    :rules="inputRulesForThreshold"
                                                                    dense
                                                                    outlined
                                                                ></v-text-field>
                                                            </v-col>
                                                            <v-col cols="auto">
                                                                <v-tooltip top>
                                                                    <template v-slot:activator="{ on, attrs }">
                                                                        <v-btn v-bind="attrs" v-on="on"
                                                                            dense                                 
                                                                            color="red"
                                                                            @click="deleteRegionRule(rule)"
                                                                        ><v-icon>mdi-delete</v-icon></v-btn>
                                                                    </template>                                 
                                                                    <span>{{ $vuetify.lang.t('$vuetify.utc.controlsCard.advancedOptionsDetail.deletethresholdexceedancealert') }}</span>
                                                                </v-tooltip>
                                                            </v-col>
                                                        </v-row>
                                                    </template>
                                                    <div v-else class="text-center mt-sm-5">{{ $vuetify.lang.t('$vuetify.utc.controlsCard.advancedOptionsDetail.nothresholdexceedancealert') }}</div>
                                                </v-card>
                                            </v-col>  <!-- Fin Depassement de seuil -->
                                            <v-divider vertical class="mt-4"></v-divider>
                                            <v-col cols="12" md="6"> <!-- Ecart de temperature -->
                                                <v-row justify="center" align="center" class="mb-2">
                                                    <v-col cols="auto">
                                                        <v-btn 
                                                            :small="$vuetify.breakpoint.xs" 
                                                            :class="this.regionRulesData.length + this.pointRulesData.length >= nbMaxAlert ? '':'gradient-wintech'" 
                                                            :disabled="this.regionRulesData.length + this.pointRulesData.length >= nbMaxAlert"
                                                            @click="addNewRegionGapRule"
                                                        >
                                                            <v-icon left>mdi-bell-plus-outline</v-icon>
                                                            {{ $vuetify.lang.t('$vuetify.utc.controlsCard.advancedOptionsDetail.temperaturedifferencealert') }}
                                                        </v-btn>
                                                    </v-col>
                                                    <v-col cols="auto">
                                                        <v-tooltip bottom :open-on-click="false" :open-on-focus="false" nudge-left="175">
                                                            <template v-slot:activator="{ on, attrs }">
                                                                <v-icon v-on="on" v-bind="attrs" :large="$vuetify.breakpoint.smAndUp" color="grey">mdi-information</v-icon>
                                                            </template>
                                                            <v-card width="650" class="mx-auto mb-2 rounded-lg">
                                                                <v-card-title class="text-h5 font-weight-bold">
                                                                    {{ $vuetify.lang.t('$vuetify.utc.controlsCard.advancedOptionsDetail.reminder')}}
                                                                </v-card-title>
                                                                <v-card-text fontcolor="black">
                                                                    <v-row class="text-h6 black--text">
                                                                        <v-col align="center">
                                                                            <div class="text-h5 font-weight-bold">{{ $vuetify.lang.t('$vuetify.utc.controlsCard.advancedOptionsDetail.gap0')}}</div>
                                                                            <div>{{ $vuetify.lang.t('$vuetify.utc.controlsCard.advancedOptionsDetail.gap1')}}</div>
                                                                            <div>{{ $vuetify.lang.t('$vuetify.utc.controlsCard.advancedOptionsDetail.gap2')}}</div>
                                                                            <div>{{ $vuetify.lang.t('$vuetify.utc.controlsCard.advancedOptionsDetail.gap3')}}</div>
                                                                            <div>{{ $vuetify.lang.t('$vuetify.utc.controlsCard.advancedOptionsDetail.gap4')}}</div>
                                                                        </v-col>
                                                                        <v-col>
                                                                            <div class="text-h5 font-weight-bold">{{ $vuetify.lang.t('$vuetify.utc.controlsCard.advancedOptionsDetail.time0')}}</div>
                                                                            <div>{{ $vuetify.lang.t('$vuetify.utc.controlsCard.advancedOptionsDetail.time1')}}</div>
                                                                            <div>{{ $vuetify.lang.t('$vuetify.utc.controlsCard.advancedOptionsDetail.time2')}}</div>
                                                                            <div>{{ $vuetify.lang.t('$vuetify.utc.controlsCard.advancedOptionsDetail.time3')}}</div>
                                                                            <div>{{ $vuetify.lang.t('$vuetify.utc.controlsCard.advancedOptionsDetail.time4')}}</div>
                                                                        </v-col>
                                                                    </v-row>
                                                                </v-card-text>
                                                            </v-card>
                                                        </v-tooltip>
                                                    </v-col>
                                                </v-row>
                                                <v-card class="overflow-y-auto overflow-x-hidden" :height="$vuetify.breakpoint.xs ? 'auto' : 280" max-height="280" flat>
                                                    <template v-if="regionGapRules.length > 0">
                                                        <v-row v-for="(rule, index) in regionGapRules" :key="index" justify="center" dense class="mt-1">
                                                            <v-col cols="4">
                                                                <v-select
                                                                    v-model="rule.items"
                                                                    :items="regionNames"
                                                                    label="Regions*"
                                                                    :rules="selectGapRule"
                                                                    multiple
                                                                    dense
                                                                    outlined
                                                                ></v-select>
                                                            </v-col>   
                                                            <v-col cols="4">
                                                                <v-text-field
                                                                    v-model="rule.value"
                                                                    :label="$vuetify.lang.t('$vuetify.utc.controlsCard.advancedOptionsDetail.difference') + ' (°C)*'"
                                                                    :rules="inputRulesForGap"
                                                                    dense
                                                                    outlined
                                                                ></v-text-field> 
                                                            </v-col>
                                                            <v-col cols="auto">
                                                                <v-tooltip top>
                                                                    <template v-slot:activator="{ on, attrs }">
                                                                        <v-btn v-bind="attrs" v-on="on"
                                                                            dense                                 
                                                                            color="red"
                                                                            @click="deleteRegionRule(rule)"
                                                                        ><v-icon>mdi-delete</v-icon></v-btn>
                                                                    </template>                                 
                                                                    <span>{{ $vuetify.lang.t('$vuetify.utc.controlsCard.advancedOptionsDetail.deletetemperaturedifferencealert') }}</span>
                                                                </v-tooltip>
                                                            </v-col>                                                                                                 
                                                        </v-row>
                                                    </template>
                                                    <div v-else class="text-center mt-sm-5">{{ $vuetify.lang.t('$vuetify.utc.controlsCard.advancedOptionsDetail.notemperaturedifferencealert') }}</div>
                                                </v-card>
                                            </v-col>
                                        </v-row>
                                    </v-container>
                                </v-tab-item>
                                <v-tab-item>
                                    <v-container> 
                                        <v-row justify="space-around" class="mb-4" :dense="$vuetify.breakpoint.smAndDown">
                                            <v-col v-if="$vuetify.breakpoint.mdAndUp" cols="auto" class="text-center" align-self="center">
                                                <h4>{{ $vuetify.lang.t('$vuetify.utc.controlsCard.advancedOptionsDetail.pointmanagement') }} ({{ pointNames.length }}/10)</h4>
                                            </v-col>
                                            <template v-if="$vuetify.breakpoint.mdAndUp">
                                                <v-col cols="auto" v-for="n in 10" :key="n">
                                                    <div class="label-on-top">
                                                        <v-label>P{{ n }}</v-label>
                                                    </div>
                                                    <v-checkbox
                                                        v-model="pointsToDelete"
                                                        :value="'' + n"
                                                        :disabled="!pointNames.includes('P' + n)"
                                                        :off-icon="!pointNames.includes('P' + n) ? 'mdi-square' : '$checkboxOff'"
                                                        :dense="$vuetify.breakpoint.smAndDown"
                                                        hide-details
                                                        class="mt-0"
                                                    ></v-checkbox>
                                                </v-col>
                                            </template>
                                            <v-col v-else cols="auto">
                                                <v-row>
                                                    <v-col cols="auto" v-for="n in 5" :key="n" class="pa-1 pa-sm-3">
                                                        <div class="label-on-top">
                                                            <v-label>P{{ n }}</v-label>
                                                        </div>
                                                        <v-checkbox
                                                            v-model="pointsToDelete"
                                                            :value="'' + n"
                                                            :disabled="!pointNames.includes('P' + n)"
                                                            :off-icon="!pointNames.includes('P' + n) ? 'mdi-square' : '$checkboxOff'"
                                                            :dense="$vuetify.breakpoint.smAndDown"
                                                            hide-details
                                                            class="mt-0"
                                                        ></v-checkbox>
                                                    </v-col>
                                                </v-row>
                                                <v-row>
                                                    <v-col cols="auto" v-for="n in 5" :key="n" class="pa-1 pa-sm-3">
                                                        <div class="label-on-top">
                                                            <v-label>P{{ (n+5) }}</v-label>
                                                        </div>
                                                        <v-checkbox
                                                            v-model="pointsToDelete"
                                                            :value="'' + (n+5)"
                                                            :disabled="!pointNames.includes('P' + (n+5))"
                                                            :off-icon="!pointNames.includes('P' + (n+5)) ? 'mdi-square' : '$checkboxOff'"
                                                            :dense="$vuetify.breakpoint.smAndDown"
                                                            hide-details
                                                            class="mt-0"
                                                        ></v-checkbox>
                                                    </v-col>
                                                </v-row>
                                            </v-col>
                                            <v-col cols="auto" align-self="center" class="pa-0">
                                                <v-tooltip top>
                                                    <template v-slot:activator="{ on, attrs }">
                                                        <v-btn v-bind="attrs" v-on="on"                                
                                                            color="red"
                                                            @click="deletePoint"
                                                            :disabled="disableDeletePoint"
                                                        ><v-icon>mdi-delete</v-icon></v-btn>
                                                    </template>
                                                    <span>{{ $vuetify.lang.t('$vuetify.utc.controlsCard.advancedOptionsDetail.deletepoint') }}</span>
                                                </v-tooltip>
                                            </v-col>
                                        </v-row>
                                        <v-divider class="mb-4"></v-divider>
                                        <v-row justify="center">
                                            <v-col cols="auto">
                                                <h4>{{ $vuetify.lang.t('$vuetify.utc.controlsCard.advancedOptionsDetail.alertmanagement') }}  ({{this.regionRulesData.length + this.pointRulesData.length}}/{{nbMaxAlert}})</h4>
                                            </v-col>
                                        </v-row>
                                        <v-row justify="space-around">
                                            <v-col cols="12" md="6"> <!-- Depassement de seuil -->
                                                <v-row justify="center" class="mb-2">
                                                    <v-col cols="auto">
                                                        <v-btn 
                                                            :small="$vuetify.breakpoint.xs" 
                                                            :class="this.regionRulesData.length + this.pointRulesData.length >= nbMaxAlert ? '':'gradient-wintech'" 
                                                            :disabled="this.regionRulesData.length + this.pointRulesData.length >= nbMaxAlert"
                                                            @click="addNewPointThresholdRule"
                                                        >
                                                            <v-icon left>mdi-bell-plus-outline</v-icon>
                                                            {{ $vuetify.lang.t('$vuetify.utc.controlsCard.advancedOptionsDetail.thresholdexceedancealert') }}
                                                        </v-btn>
                                                    </v-col>
                                                </v-row>
                                                <v-card class="overflow-y-auto overflow-x-hidden" :height="$vuetify.breakpoint.xs ? 'auto' : 280" max-height="280" flat>
                                                    <template v-if="pointThresholdRules.length > 0">
                                                        <v-row v-for="(rule, index) in pointThresholdRules" :key="index" justify="center" class="mt-1" dense>
                                                            <v-col cols="4">
                                                                <v-select
                                                                    v-model="rule.items"
                                                                    :items="pointNames"
                                                                    :rules="selectThresholdRule"
                                                                    label="Point*"
                                                                    dense
                                                                    outlined
                                                                ></v-select>
                                                            </v-col>
                                                            <v-col cols="4">
                                                                <v-text-field
                                                                    v-model="rule.value"
                                                                    :label="$vuetify.lang.t('$vuetify.utc.controlsCard.advancedOptionsDetail.threshold') + ' (°C)*'"
                                                                    :rules="inputRulesForThreshold"
                                                                    dense
                                                                    outlined
                                                                ></v-text-field>
                                                            </v-col>
                                                            <v-col cols="auto">
                                                                <v-tooltip top>
                                                                    <template v-slot:activator="{ on, attrs }">
                                                                        <v-btn v-bind="attrs" v-on="on"
                                                                            dense                                 
                                                                            color="red"
                                                                            @click="deletePointRule(rule)"
                                                                        ><v-icon>mdi-delete</v-icon></v-btn>
                                                                    </template>                                 
                                                                    <span>{{ $vuetify.lang.t('$vuetify.utc.controlsCard.advancedOptionsDetail.deletethresholdexceedancealert') }}</span>
                                                                </v-tooltip>
                                                            </v-col>
                                                        </v-row>
                                                    </template>
                                                    <div v-else class="text-center mt-sm-5">{{ $vuetify.lang.t('$vuetify.utc.controlsCard.advancedOptionsDetail.nothresholdexceedancealert') }}</div>
                                                </v-card>
                                            </v-col>  <!-- Fin Depassement de seuil -->
                                            <v-divider vertical class="mt-4"></v-divider>
                                            <v-col cols="12" md="6"> <!-- Ecart de temperature -->
                                                <v-row justify="center" align="center" class="mb-2">
                                                    <v-col cols="auto">
                                                        <v-btn 
                                                            :small="$vuetify.breakpoint.xs" 
                                                            :class="this.regionRulesData.length + this.pointRulesData.length >= nbMaxAlert ? '':'gradient-wintech'" 
                                                            :disabled="this.regionRulesData.length + this.pointRulesData.length >= nbMaxAlert"
                                                            @click="addNewPointGapRule"
                                                        >
                                                            <v-icon left>mdi-bell-plus-outline</v-icon>
                                                            {{ $vuetify.lang.t('$vuetify.utc.controlsCard.advancedOptionsDetail.temperaturedifferencealert') }}
                                                        </v-btn>
                                                    </v-col>
                                                    <v-col cols="auto">
                                                        <v-tooltip bottom :open-on-click="false" :open-on-focus="false" nudge-left="175">
                                                            <template v-slot:activator="{ on, attrs }">
                                                                <v-icon v-on="on" v-bind="attrs" :large="$vuetify.breakpoint.smAndUp" color="grey">mdi-information</v-icon>
                                                            </template>
                                                            <v-img src="@/assets/gapGuide.png"></v-img>
                                                        </v-tooltip>
                                                    </v-col>
                                                </v-row>
                                                <v-card class="overflow-y-auto overflow-x-hidden" :height="$vuetify.breakpoint.xs ? 'auto' : 280" max-height="280" flat>
                                                    <template v-if="pointGapRules.length > 0">
                                                        <v-row v-for="(rule, index) in pointGapRules" :key="index" justify="center" dense class="mt-1">
                                                            <v-col cols="4">
                                                                <v-select
                                                                    v-model="rule.items"
                                                                    :items="pointNames"
                                                                    label="Points*"
                                                                    :rules="selectGapRule"
                                                                    multiple
                                                                    dense
                                                                    outlined
                                                                ></v-select>
                                                            </v-col>   
                                                            <v-col cols="4">
                                                                <v-text-field
                                                                    v-model="rule.value"
                                                                    :label="$vuetify.lang.t('$vuetify.utc.controlsCard.advancedOptionsDetail.difference') + ' (°C)*'"
                                                                    :rules="inputRulesForGap"
                                                                    dense
                                                                    outlined
                                                                ></v-text-field> 
                                                            </v-col>
                                                            <v-col cols="auto">
                                                                <v-tooltip top>
                                                                    <template v-slot:activator="{ on, attrs }">
                                                                        <v-btn v-bind="attrs" v-on="on"
                                                                            dense                                 
                                                                            color="red"
                                                                            @click="deletePointRule(rule)"
                                                                        ><v-icon>mdi-delete</v-icon></v-btn>
                                                                    </template>                                 
                                                                    <span>{{ $vuetify.lang.t('$vuetify.utc.controlsCard.advancedOptionsDetail.deletetemperaturedifferencealert') }}</span>
                                                                </v-tooltip>
                                                            </v-col>                                                                                                 
                                                        </v-row>
                                                    </template>
                                                    <div v-else class="text-center mt-sm-5">{{ $vuetify.lang.t('$vuetify.utc.controlsCard.advancedOptionsDetail.notemperaturedifferencealert') }}</div>
                                                </v-card>
                                            </v-col>
                                        </v-row>
                                    </v-container>
                                </v-tab-item>
                            </v-tabs-items>
                        </v-col>
                    </v-row>
                    <v-row justify="center" align="center" class="mt-0">
                        <v-col cols="auto">
                            <v-alert :width="webImageWidth" :height="$vuetify.breakpoint.width < 540 ? 'auto' : 58" class="gradient-wintech text-body-2 mb-0" dense outlined icon="$info">
                                {{ isRegionToolActive ? $vuetify.lang.t('$vuetify.utc.controlsCard.advancedOptionsDetail.createregioninfo') : $vuetify.lang.t('$vuetify.utc.controlsCard.advancedOptionsDetail.createpointinfo') }}
                            </v-alert>
                        </v-col>
                        <v-col :style="{ maxWidth: ($vuetify.breakpoint.width > 1830 ? 956 : (webImageWidth + 2*12)) + 'px' }">
                            <v-card-actions class="px-0">
                                <v-tooltip top>
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn
                                            v-bind="attrs"
                                            v-on="on"
                                            fab
                                            :small="$vuetify.breakpoint.xs"
                                            class="gradient-wintech"
                                            @click="takeScreenshot"
                                        >
                                            <v-icon>mdi-camera-wireless</v-icon>
                                        </v-btn>
                                    </template>
                                    <span>{{ $vuetify.lang.t('$vuetify.utc.controlsCard.advancedOptionsDetail.takeapictureremotely') }}</span>
                                </v-tooltip>
                                <v-spacer />
                                <v-btn  
                                    :disabled="!hasDataChanged || !isFormValid"
                                    :class="!hasDataChanged || !isFormValid ? '' : 'gradient-wintech'"
                                    :small="$vuetify.breakpoint.xs"
                                    @click="sendToUTC"
                                >
                                    <v-icon left>mdi-send-outline</v-icon>
                                    {{ $vuetify.lang.t('$vuetify.utc.controlsCard.advancedOptionsDetail.sendtoutc') }}
                                </v-btn>
                                <v-btn
                                    :small="$vuetify.breakpoint.xs"
                                    color="red"
                                    @click="close"
                                >
                                    <v-icon left>mdi-cancel</v-icon>
                                    {{ $vuetify.lang.t('$vuetify.utc.controlsCard.advancedOptionsDetail.cancel') }}
                                </v-btn>
                            </v-card-actions>
                        </v-col>
                    </v-row>
                </v-form>
            </v-container>
        </v-card>
    </v-dialog>
</template>
<script>

import axios from 'axios';
import { mapGetters } from 'vuex';
import cv from '../../../../plugins/opencv';
import MessageOverlay from '@/components/Overlay/MessageOverlay.vue'
import _ from 'lodash';
var cloneDeep = require('lodash.clonedeep');

export default {

    name: "AdvancedOptionsDetail",
    components :{ MessageOverlay},

    props: {
        open: Boolean,
        serialnb: String,
        lastScreenshot: Object,
        regions: Array,
        points: Array,
        masks: Array,
        regionRules: Array,
        pointRules: Array,
       
    },

    data() {
        return {
            isFormValid: false,
            loading: true,
            utcImageWidth: 170,
            utcImageHeight: 120,
            utcColormapWidth: 10,

            tab: 0,
            isRegionToolActive: true,
            isPointToolActive: false,
            
            topLeftBottomRight: [],

            regionsData: cloneDeep(this.regions),
            pointsData: cloneDeep(this.points),
            regionRulesData: cloneDeep(this.regionRules),
            pointRulesData: cloneDeep(this.pointRules),
            regionsToDelete: [],
            pointsToDelete: [],

            nbMaxAlert: 20,
        }
    },

    computed  : {
        ...mapGetters({ user: "StateUser" }),
        isConsultation() {
            return this.user.role === 'CONSULTATION'
        },
        isTiff() {
            return this.lastScreenshot.tiff !== undefined
        },
        webImageWidth() {
            return Math.min(this.$vuetify.breakpoint.width - (2 * 12) - (this.$vuetify.breakpoint.smAndUp ? (2 * 24) : 0), 800)
        },
        webImageHeight() {
            return this.webImageWidth * 565 / 800
        },
        webColormapWidth() {
            return this.utcColormapWidth*this.webImageWidth/this.utcImageWidth
        },
        regionNames() {
            return this.regionsData.map(region => "R" + region.name).sort();
        },
        pointNames() {
            return this.pointsData.map(point => "P" + point.name).sort((a, b) => parseInt(a.substr(1)) - parseInt(b.substr(1)));
        },
        regionThresholdRules() {
            return this.regionRulesData.filter(rule => rule.type === 1);
        },
        pointThresholdRules() {
            return this.pointRulesData.filter(rule => rule.type === 2);
        },
        regionGapRules() {
            return this.regionRulesData.filter(rule => rule.type === 3);
        },
        pointGapRules() {
            return this.pointRulesData.filter(rule => rule.type === 4);
        },
        disableDeleteRegion() {
            return this.regionsToDelete.length === 0;
        },
        disableDeletePoint() {
            return this.pointsToDelete.length === 0;
        },
        inputRulesForThreshold() {
            return [
                v => !!v || this.$vuetify.lang.t('$vuetify.utc.controlsCard.advancedOptionsDetail.avalueisrequired'),
                v => /^\d+(?:\.\d{1,2})?$/.test(v)  || this.$vuetify.lang.t('$vuetify.utc.controlsCard.advancedOptionsDetail.thethresholdvaluemustbeapositiveintegernumber'),
            ]
        },
        inputRulesForGap() {
            return [
                v => !!v || this.$vuetify.lang.t('$vuetify.utc.controlsCard.advancedOptionsDetail.avalueisrequired'),
                v => /^\d+(?:\.\d{1,2})?$/.test(v)  || this.$vuetify.lang.t('$vuetify.utc.controlsCard.advancedOptionsDetail.thegapvaluemustbeapositiveintegernumber'),
            ]
        },
        selectThresholdRule() {
            return [
                v => !!v && v !== "" || this.$vuetify.lang.t('$vuetify.utc.controlsCard.advancedOptionsDetail.avalueisrequired')
            ]
        },
        selectGapRule() {
            return [
                v => v.length >= 2 || this.$vuetify.lang.t('$vuetify.utc.controlsCard.advancedOptionsDetail.youhavetochooseatleast2items'),
            ]
        },
        hasDataChanged() {
            return !(_.isEqual(this.regions, this.regionsData) && _.isEqual(this.points, this.pointsData) && _.isEqual(this.regionRules, this.regionRulesData) && _.isEqual(this.pointRules, this.pointRulesData))
        }
    },

    watch: {
        lastScreenshot: {
            async handler(newScreenshot) {
                this.loading = true;
                setTimeout(() => {
                    let input = document.getElementById('canvasInput');
                    if (newScreenshot.tiff !== undefined) {
                        this.loadOutput();
                    } else {
                        input.src = newScreenshot.url;
                    }
                    this.loading = false;
                }, 1000)
            },
            flush: 'post'
        },
        open (newOpen) {
            newOpen && setTimeout(() => {
                let input = document.getElementById('canvasInput');
                if (this.isTiff) {
                    this.loadOutput();
                } else {
                    input.src = this.lastScreenshot.url;
                }
                this.loading = false;
            }, 1000)
        },
    },

    methods: {
        changeTool() {
            switch(this.tab) {
                case 0: {
                    this.selectRegionTool();
                    break;
                }
                case 1: {
                    this.selectPointTool();
                    break;
                }
            }
        },

        close() {
            this.selectRegionTool();
            this.$emit('close');
        },

        async takeScreenshot() {
            try {
                const res = await axios.post('utcs/'+ this.serialnb +'/takeScreenshot');
                if (res.status == 200) {
                    this.$refs.overlay.showOverlay("success", this.$vuetify.lang.t('$vuetify.utc.controlsCard.advancedOptionsDetail.screenshotOrdersentOK'))
                }
            } catch(err) {
                this.$refs.overlay.showOverlay("error", this.$vuetify.lang.t('$vuetify.utc.controlsCard.advancedOptionsDetail.screenshotOrdersentERR'))
            }
        },

        selectRegionTool() {
            if (!this.isRegionToolActive) {
                this.tab = 0;
                this.isPointToolActive = false;
                this.isRegionToolActive = true;
                this.reset();
            }
        },

        selectPointTool() {
            if (!this.isPointToolActive) {
                this.tab = 1;
                this.isRegionToolActive = false;
                this.isPointToolActive = true;
                this.reset();
            }
        },

        addNewRegionThresholdRule() {
            let rule = {
                type: 1,
                items: "",
                value: ""
            };
            this.regionRulesData.push(rule);
        },

        addNewPointThresholdRule() {
            let rule = {
                type: 2,
                items: "",
                value: ""
            };
            this.pointRulesData.push(rule);
        },

        addNewRegionGapRule() {
            let rule = {
                type: 3,
                items: [],
                value: ""
            };
            this.regionRulesData.push(rule);
        },

        addNewPointGapRule() {
            let rule = {
                type: 4,
                items: [],
                value: ""
            };
            this.pointRulesData.push(rule);
        },

        deleteRegionRule(rule) {
            if (this.regionRulesData.includes(rule)) this.regionRulesData.splice(this.regionRulesData.indexOf(rule), 1);
        },

        deletePointRule(rule) {
            if (this.pointRulesData.includes(rule)) this.pointRulesData.splice(this.pointRulesData.indexOf(rule), 1);
        },

        xToUTCScale(x) {
            return Math.round(x*(this.utcImageWidth/this.webImageWidth))
        },

        yToUTCScale(y) {
            return Math.round(y*(this.utcImageHeight/this.webImageHeight))
        },

        xToWebScale(x) {
            return Math.round(x*(this.webImageWidth - this.webColormapWidth)/160)
        },

        yToWebScale(y) {
            return Math.round(y*(this.webImageHeight/this.utcImageHeight))
        },

        drawRectangle(nature, mat, rectangle, color, type) {
            let tl = new cv.Point(this.xToWebScale(rectangle.x_tl), this.xToWebScale(rectangle.y_tl));
            let br = new cv.Point(this.xToWebScale(rectangle.x_tl + rectangle.width), this.xToWebScale(rectangle.y_tl + rectangle.height));
            const text = nature + rectangle.name;
            cv.rectangle(mat, tl, br, color, type);
            cv.putText(mat, text, { x: this.xToWebScale(rectangle.x_tl), y: this.xToWebScale(rectangle.y_tl) - 3 }, cv.FONT_HERSHEY_DUPLEX, 0.5, color);
        },

        drawPoint(mat, point, color) {
            let start1 = new cv.Point(this.xToWebScale(point.x) - 7, this.yToWebScale(point.y));
            let end1 = new cv.Point(this.xToWebScale(point.x) + 7, this.yToWebScale(point.y));
            let start2 = new cv.Point(this.xToWebScale(point.x), this.yToWebScale(point.y) - 7);
            let end2 = new cv.Point(this.xToWebScale(point.x), this.yToWebScale(point.y) + 7);
            cv.line(mat, start1, end1, color, 2, cv.LINE_AA, 0);
            cv.line(mat, start2, end2, color, 2, cv.LINE_AA, 0);
            cv.putText(mat, "P" + point.name, { x: this.xToWebScale(point.x) + 2, y: this.yToWebScale(point.y) + 15 }, cv.FONT_HERSHEY_DUPLEX, 0.5, color);
        },
        
        addNewRegion(point) {
            if (this.regionsData.length < 5) {
                this.topLeftBottomRight.push(point);
                if (this.topLeftBottomRight.length%2 === 0) {
                    let newRectangle = {};
                    if (this.topLeftBottomRight[0].x<this.topLeftBottomRight[1].x && this.topLeftBottomRight[0].y<this.topLeftBottomRight[1].y) {
                        newRectangle.x_tl = this.xToUTCScale(this.topLeftBottomRight[0].x);
                        newRectangle.y_tl = this.yToUTCScale(this.topLeftBottomRight[0].y);
                    }
                    else if (this.topLeftBottomRight[0].x>this.topLeftBottomRight[1].x && this.topLeftBottomRight[0].y>this.topLeftBottomRight[1].y) {
                        newRectangle.x_tl = this.xToUTCScale(this.topLeftBottomRight[1].x);
                        newRectangle.y_tl = this.yToUTCScale(this.topLeftBottomRight[1].y);
                    }
                    else if (this.topLeftBottomRight[0].x<this.topLeftBottomRight[1].x && this.topLeftBottomRight[0].y>this.topLeftBottomRight[1].y) {
                        newRectangle.x_tl = this.xToUTCScale(this.topLeftBottomRight[0].x);
                        newRectangle.y_tl = this.yToUTCScale(this.topLeftBottomRight[1].y);
                    }
                    else if (this.topLeftBottomRight[0].x>this.topLeftBottomRight[1].x && this.topLeftBottomRight[0].y<this.topLeftBottomRight[1].y) {
                        newRectangle.x_tl = this.xToUTCScale(this.topLeftBottomRight[1].x);
                        newRectangle.y_tl = this.yToUTCScale(this.topLeftBottomRight[0].y);
                    }
                    let width = Math.abs(this.xToUTCScale(this.topLeftBottomRight[1].x) - this.xToUTCScale(this.topLeftBottomRight[0].x))
                    let height = Math.abs(this.yToUTCScale(this.topLeftBottomRight[1].y) - this.yToUTCScale(this.topLeftBottomRight[0].y))
                    newRectangle.width = width;
                    newRectangle.height = height;
                    let usedNumbers = this.regionsData.map(obj => parseInt(obj.name))
                    let smallestUnusedNumber = 1
                    while (usedNumbers.includes(smallestUnusedNumber)) {
                        smallestUnusedNumber++;
                    }
                    newRectangle.name = "" + smallestUnusedNumber;
                    newRectangle.serialnb = this.serialnb;
                    this.regionsData.push(newRectangle);
                    this.topLeftBottomRight = [];
                }
            } else {
                this.$refs.overlay.showOverlay("error", this.$vuetify.lang.t('$vuetify.utc.controlsCard.advancedOptionsDetail.youwillexceedthenumberofauthorizedareas'))
            }
        },

        addNewPoint(point) {
            if (this.pointsData.length < 10) {
                let usedPointNamesAsInt = this.pointsData.map(point => parseInt(point.name))
                let smallestPointNameAvailable = 1
                while (usedPointNamesAsInt.includes(smallestPointNameAvailable)) {
                    smallestPointNameAvailable++;
                }
                this.pointsData.push({
                    x: this.xToUTCScale(point.x),
                    y: this.yToUTCScale(point.y),
                    name: "" + smallestPointNameAvailable,
                    serialnb: this.serialnb
                })
            } else {
                this.$refs.overlay.showOverlay("error", this.$vuetify.lang.t('$vuetify.utc.controlsCard.advancedOptionsDetail.youwillexceedthenumberofauthorizedpoints'))
            }
        },

        drawElements(outputMat) {        
            for(const mask of this.masks) {      
                this.drawRectangle("M", outputMat, mask, new cv.Scalar(255, 0, 0, 255), 2)
                if (mask.restrictAlarm == "1") {
                    let lineColor = new cv.Scalar(255, 0, 0, 255); 
                    let tl = new cv.Point(this.xToWebScale(mask.x_tl), this.yToWebScale(mask.y_tl));
                    let tr = new cv.Point(this.xToWebScale(mask.x_tl + mask.width), this.yToWebScale(mask.y_tl));
                    let br = new cv.Point(this.xToWebScale(mask.x_tl + mask.width), this.yToWebScale(mask.y_tl + mask.height));
                    let bl = new cv.Point(this.xToWebScale(mask.x_tl), this.yToWebScale(mask.y_tl + mask.height));
                    cv.line(outputMat, tl, br, lineColor, 2, cv.LINE_AA);
                    cv.line(outputMat, bl, tr, lineColor, 2, cv.LINE_AA);
                }
            }
            for(const region of this.regionsData) {
                this.drawRectangle("R", outputMat, region, new cv.Scalar(255, 255, 255, 255), 2)       
            }
            for(const point of this.pointsData) {
                this.drawPoint(outputMat, point, new cv.Scalar(255, 255, 255, 255))
            }
        },

        loadOutput() {
            if (this.lastScreenshot.url !== undefined) {
                var src;
                if (this.isTiff) {
                    src = cv.matFromArray(this.utcImageHeight, this.utcImageWidth, cv.CV_8UC3, this.lastScreenshot.tiff[0].data);
                } else {
                    let input = document.getElementById('canvasInput');
                    src = cv.imread(input);
                }
                let dsize = new cv.Size(this.webImageWidth, this.webImageHeight);
                let mat = new cv.Mat.zeros(src.rows, src.cols, cv.CV_8UC3);
                cv.resize(src, mat, dsize, 0, 0, cv.INTER_AREA);
                let output = document.getElementById('canvasOutput');
                this.drawElements(mat);
                cv.imshow(output, mat);
                src.delete(); mat.delete();
            }
        },

        drawOnImage(event) {
            if (this.lastScreenshot.url !== undefined) {
                var src;
                if (this.isTiff) {
                    src = cv.matFromArray(this.utcImageHeight, this.utcImageWidth, cv.CV_8UC3, this.lastScreenshot.tiff[0].data);
                } else {
                    let input = document.getElementById('canvasInput');
                    src = cv.imread(input);
                }
                let dsize = new cv.Size(this.webImageWidth, this.webImageHeight);
                let mat = new cv.Mat.zeros(src.rows, src.cols, cv.CV_8UC3);
                cv.resize(src, mat, dsize, 0, 0, cv.INTER_AREA);
                if (mat.isContinuous() && event.offsetX <= 160*mat.cols/this.utcImageWidth) {
                    let point = new cv.Point(event.offsetX, event.offsetY)
                    if (this.isRegionToolActive) {
                        this.addNewRegion(point);
                    } else if (this.isPointToolActive) {
                        this.addNewPoint(point);
                    }
                }
                this.drawElements(mat);
                let output = document.getElementById('canvasOutput');
                cv.imshow(output, mat)
                src.delete(); mat.delete();
            }
        },

        async sendToUTC() {
            if (this.$refs.form.validate()) {
                let dataRPM = "";
    
                dataRPM += "\"P\":\"";
                for (const point of this.pointsData) {
                    dataRPM += point.name + "|";
                    dataRPM += Math.round(point.x) + "|" + Math.round(point.y) + "|n"
                }
    
                dataRPM += "\",\"R\":\"";
                for (const region of this.regionsData) {
                    dataRPM += region.name + "|";
                    dataRPM += Math.round(region.x_tl) + "|" + Math.round(region.y_tl) + "|" + Math.round(region.width) + "|" + Math.round(region.height) + "|n";
                }
    
                dataRPM += "\",\"M\":\"";
                for (const mask of this.masks) {
                    dataRPM += mask.name + "|";
                    dataRPM += Math.round(mask.x_tl) + "|" + Math.round(mask.y_tl) + "|" + Math.round(mask.width) + "|" + Math.round(mask.height) + "|" + (+mask.restrictAlarm) + "|n";
                }
    
                dataRPM += "\",\"RU\":\"";
                let allRules = this.regionRulesData.concat(this.pointRulesData);
                for (const rule of allRules) {
                    dataRPM += rule.type + "|" + (typeof(rule.items) === "string" ? rule.items.replace(/R|P/g, "") : rule.items.join(";").replace(/R|P/g, "")) + "|" + rule.value + "|n";
                }
                dataRPM += "\"";
    
                try {
                    const res = await axios.post('areapoint/rpm', {
                        serialnb: this.serialnb,
                        data: dataRPM
                    });
                    if (res.status == 200) {
                        this.$refs.overlay.showOverlay("success", this.$vuetify.lang.t('$vuetify.utc.controlsCard.advancedOptionsDetail.yourorderhasbeensenttoUTC'))
                        this.$emit("update:regions", cloneDeep(this.regionsData));
                        this.$emit("update:points", cloneDeep(this.pointsData));
                        this.$emit("update:regionRules", cloneDeep(this.regionRulesData));
                        this.$emit("update:pointRules", cloneDeep(this.pointRulesData));
                        
                    }
                } catch(err) {
                    console.error(err)
                    this.$refs.overlay.showOverlay("error", this.$vuetify.lang.t('$vuetify.utc.controlsCard.advancedOptionsDetail.yourordercouldnotbesenttoUTC'))
                }
    
                this.reset();
            } else {
                this.$refs.overlay.showOverlay("error", this.$vuetify.lang.t('$vuetify.utc.controlsCard.advancedOptionsDetail.anerrorexistsinalertsconfiguration'))
            }
        },

        deleteRegion() {
            let regionsNamesToBeDeleted = this.regionsData.filter((region) => this.regionsToDelete.includes(region.name)).map(region => "R" + region.name);
            if (regionsNamesToBeDeleted.every(region => !this.regionRulesData.map(rule => [rule.items]).flat(2).includes(region))) {
                this.regionsData = this.regionsData.filter((region) => !this.regionsToDelete.includes(region.name));
                this.loadOutput();
                this.regionsToDelete = [];
            } else {
                this.$refs.overlay.showOverlay("error", this.$vuetify.lang.t('$vuetify.utc.controlsCard.advancedOptionsDetail.atleastoneoftheregionstobedeleted'))
            }
        },

        deletePoint() {
            let pointsNamesToBeDeleted = this.pointsData.filter((point) => this.pointsToDelete.includes(point.name)).map(point => "P" + point.name);
            if (pointsNamesToBeDeleted.every(point => !this.pointRulesData.map(rule => [rule.items]).flat(2).includes(point))) {
                this.pointsData = this.pointsData.filter((point) => !this.pointsToDelete.includes(point.name));
                this.loadOutput();
                this.pointsToDelete = [];
            } else {
                this.$refs.overlay.showOverlay("error", this.$vuetify.lang.t('$vuetify.utc.controlsCard.advancedOptionsDetail.atleastoneofthepointstobedeleted'))
            }
        },

        reset() {
            this.topLeftBottomRight = [];
            this.loadOutput();
        },
    },
}
</script>
<style scoped>
.label-on-top {
    margin-left: 2px !important;
}
</style>